import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell } from "lucide-react";
import Sidebar from "./Sidebar";
import SidebarToggle from "./SidebarToggle";
import ProjectPicker from "../ProjectPicker";

export default function WorkspaceShell({ children }) {
  const navigate = useNavigate();

  // 1. Collapsed state management with localStorage
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });

  // 2. Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", collapsed);
    window.dispatchEvent(new CustomEvent("sidebar-toggle", { detail: collapsed }));
  }, [collapsed]);

  // 3. Keyboard shortcut Ctrl+B / Cmd+B to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 4. ESC key to close mobile sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };
    if (mobileOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen]);

  // 5. Close mobile drawer on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 6. Handle New Chat action (routing + custom event)
  const handleNewChat = () => {
    if (window.location.pathname !== "/workspace") {
      navigate("/workspace");
      // Wait for page to mount and register event listener
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("new-chat"));
      }, 150);
    } else {
      window.dispatchEvent(new CustomEvent("new-chat"));
    }
    setMobileOpen(false);
  };

  // 7. Sync transitions
  const contentStyle = {
    transition: "margin-left 0.28s ease",
  };

  return (
    <div className="min-h-screen w-full max-w-full bg-[#FAFAFC] text-gray-900 overflow-x-hidden" data-testid="workspace-shell">
      {/* Sidebar Panel */}
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onNewChat={handleNewChat}
      />

      {/* Main Content Area with sync transition */}
      <div
        style={contentStyle}
        className={`flex-1 flex flex-col min-h-screen w-full max-w-full overflow-x-hidden ${
          collapsed ? "lg:ml-[72px] lg:w-[calc(100%-72px)]" : "lg:ml-[260px] lg:w-[calc(100%-260px)]"
        } ml-0`}
      >
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-md border-b border-gray-100 h-16 flex items-center justify-between px-6 md:px-8 w-full max-w-full">
          <div className="flex items-center gap-3">
            {/* Sidebar toggle button (desktop toggle / mobile toggle) */}
            <div className="hidden lg:block">
              <SidebarToggle
                collapsed={collapsed}
                onClick={() => setCollapsed(!collapsed)}
              />
            </div>
            <div className="lg:hidden">
              <SidebarToggle
                collapsed={!mobileOpen}
                onClick={() => setMobileOpen(!mobileOpen)}
              />
            </div>
          </div>

          {/* Search bar */}
          <div className="hidden md:flex items-center gap-2.5 px-4 py-2 rounded-full bg-white border border-gray-200 text-xs text-gray-500 flex-1 max-w-md shadow-xs transition-all hover:border-[#7C3AED]/30 focus-within:border-[#7C3AED]/60 focus-within:ring-2 focus-within:ring-[#7C3AED]/5">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search across your workspace…"
              className="bg-transparent border-none outline-none text-gray-900 placeholder-gray-400 w-full text-xs font-medium"
              aria-label="Search"
            />
          </div>

          {/* Right Header elements */}
          <div className="flex items-center gap-3">
            <button
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/10"
              aria-label="Notifications"
            >
              <Bell className="w-4.5 h-4.5" />
            </button>
            <div className="border-l border-gray-100 h-5 my-auto" />
            <ProjectPicker />
          </div>
        </header>

        {/* Content body */}
        <main className="flex-1 w-full max-w-full overflow-x-hidden px-4 sm:px-5 md:px-8 pt-8 pb-16">
          {children}
        </main>
      </div>
    </div>
  );
}
